// Условия списка «Выкатки» в адресе страницы (T158; README «Плотные таблицы»; ux.md, раздел 6):
// поиск по версии и коммиту, «Фильтр» — копия перед выкаткой и кто выкатил, сортировка по времени
// выкатки. Выкаток немного (сервер отдаёт последние DEPLOYMENTS_LIMIT = 200 — за годы пилота),
// поэтому условия применяются к загруженному списку в браузере, без параметров GET
// /api/ops/deployments. Итога или состояния у выкатки нет (observability.deployments): выкатка
// в журнале — уже выполненная; сужают список копия перед выкаткой (была — не было) и учётная
// запись, с которой выкатили.
import type { Deployment } from '@kp/contracts';

export type DeploymentSort = 'deployedAt' | '-deployedAt';

/** Копия перед выкаткой: была или не было. */
export type DeploymentBackup = 'yes' | 'no';

export interface DeploymentsSearch {
  /** Часть версии или коммита, как её ввели: «0.2», «c3d4». */
  q?: string;
  backup?: DeploymentBackup;
  /** Кто выкатил: учётная запись на стенде. */
  by?: string;
  sort?: DeploymentSort;
}

/** Новые сверху — по умолчанию: в адрес не пишется. */
export const DEFAULT_DEPLOYMENTS_SORT: DeploymentSort = '-deployedAt';

/** Длина поиска: версия и короткий коммит короче. */
export const DEPLOYMENTS_SEARCH_MAX = 64;

/** Для адреса страницы: validateSearch маршрута /ops/deployments. */
export function parseDeploymentsSearch(search: Record<string, unknown>): DeploymentsSearch {
  const result: DeploymentsSearch = {};
  if (typeof search.q === 'string' && search.q.trim() !== '') {
    result.q = search.q.slice(0, DEPLOYMENTS_SEARCH_MAX);
  }
  if (search.backup === 'yes' || search.backup === 'no') result.backup = search.backup;
  if (typeof search.by === 'string' && search.by.trim() !== '') result.by = search.by;
  if (search.sort === 'deployedAt' || search.sort === '-deployedAt') result.sort = search.sort;
  return result;
}

/** Условия сужают список: «Ничего не найдено» вместо «Выкаток пока нет». */
export function hasDeploymentFilters(search: DeploymentsSearch): boolean {
  return Boolean(search.q?.trim() || search.backup || search.by);
}

/** Сколько условий фильтра выбрано: «Фильтр · 2». Поиск — не условие фильтра. */
export function deploymentFilterCount(search: DeploymentsSearch): number {
  return (search.backup ? 1 : 0) + (search.by ? 1 : 0);
}

/**
 * Выкатки по условиям: часть версии или коммита без учёта регистра, копия, кто выкатил;
 * по времени выкатки — новые или старые сверху.
 */
export function filterDeployments(
  rows: readonly Deployment[],
  search: DeploymentsSearch,
): Deployment[] {
  const needle = search.q?.trim().toLocaleLowerCase('ru-RU') ?? '';
  const found = rows.filter(
    (row) =>
      (needle === '' ||
        row.version.toLocaleLowerCase('ru-RU').includes(needle) ||
        row.commit.toLocaleLowerCase('ru-RU').includes(needle)) &&
      (search.backup === undefined ||
        (row.backupRef !== undefined) === (search.backup === 'yes')) &&
      (search.by === undefined || row.deployedBy === search.by),
  );
  const desc = (search.sort ?? DEFAULT_DEPLOYMENTS_SORT) === '-deployedAt';
  return found.sort((a, b) =>
    desc ? b.deployedAt.localeCompare(a.deployedAt) : a.deployedAt.localeCompare(b.deployedAt),
  );
}

/**
 * Кто выкатывал: варианты фильтра «Кто выкатил» из загруженного списка, по алфавиту. Выбранная
 * в адресе учётная запись — среди вариантов и тогда, когда список ещё грузится, не загрузился
 * или её выкаток в нём нет: выбор виден отмеченным, его можно снять (T161).
 */
export function deployers(rows: readonly Deployment[], selected?: string): string[] {
  const accounts = new Set(rows.map((row) => row.deployedBy));
  if (selected) accounts.add(selected);
  return [...accounts].sort((a, b) => a.localeCompare(b, 'ru'));
}
